import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { directory, documentation } from '@cwos/api-client';
import { Pill, Refused, ScopeNote, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import {
  figureNote,
  figurePhase,
  figureText,
  narrowedTo,
  ORGANIZATION,
  UNKNOWN_POPULATION,
  type FigurePhase,
  type Population,
} from '../figureState.js';
import { listPhase, nameResolver } from '../listState.js';
import { ListState } from '../ListStates.js';
import { useNav } from '../navigation.js';
import {
  completenessBlurb,
  completenessLabel,
  completenessPillVariant,
  COMPLETENESS_VALUES,
  dueBasisNote,
  emptyMessage,
  NOT_MEASURABLE,
  pageCovers,
  rollupByWorker,
  STATUS_ONLY,
  type Completeness,
  type WorkerRollup,
} from './completenessLogic.js';

/**
 * DOCUMENTATION COMPLETENESS — the metadata surface, for the person whose job
 * it is.
 *
 * ============================================================================
 * WHY THIS SCREEN EXISTS
 * ============================================================================
 * `GET /documentation/completeness` shipped with migration `1834000000000`:
 * its own key (`documentation:read_metadata`, seeded to `org_admin` and
 * `supervisor`), its own scope resolution, a projection that cannot carry a
 * note, and an integration suite proving all of it. Two callers used it —
 * `agent-runtime/productionAdapters.ts` and `docFollowUpTools.ts`. Neither is a
 * person. `grep -rn "completeness" packages/api-client/src/` returned zero.
 *
 * That is founder ruling 7 in reverse: documentation metadata reaches
 * Intelligence and Agents, and does not reach the supervisor. The model could
 * ask how much documentation was outstanding; the supervisor accountable for it
 * could not. Ops Console ran `checkUncharted`, `watchCharting` and
 * `nudgeUnchartedToday` on a schedule for exactly this question, and the
 * `charting_open` tab was the answer a human read. This screen is that tab.
 *
 * ============================================================================
 * WHAT THIS SCREEN IS NOT
 * ============================================================================
 * A METADATA surface, not a clinical one. Phibian may know documentation
 * STATUS; it must never become where the document is authored. Nothing here
 * renders a note, links to one, or offers to write one — and the response has
 * no field that could. The one link out of this screen is to
 * `/documentation`, which is the RECORDS surface behind a different key
 * (`documentation:read`), so a reader who may count notes but not read them
 * cannot arrive at one from here.
 *
 * The key is NARROWER than `documentation:read`, not wider — see
 * `packages/api-client/src/documentation.ts:completeness`. So this route is
 * gated on `documentation:read_metadata` and on nothing else. Charging a second
 * key the server does not check would refuse readers the server would serve.
 *
 * ============================================================================
 * THE WORKER HALF IS NOT HERE, AND THAT IS DELIBERATE
 * ============================================================================
 * Migration `1834000000000` says it in as many words: the metadata key is NOT
 * granted to `worker`, because "a worker meets their own outstanding
 * documentation as the to-write queue, which is a list of work, not as a
 * completeness report about people." That half already exists and is reachable
 * — `documentation.toWrite()` is bound in `@cwos/api-client`, rendered by
 * `apps/worker-mobile/app/(tabs)/to-write.tsx` and by `worker-web/WorkerWeb`,
 * and ranked as a priority by `now-next.ts:noteItems`. Nothing was built for it
 * here. Management sees intelligence; workers see priorities.
 *
 * ============================================================================
 * THE THREE NUMBERS PROBLEM, WHICH THIS SCREEN INHERITS AND STATES
 * ============================================================================
 * `counts` is computed SERVER-SIDE over the whole scoped set. `records` is one
 * page of at most 200. The per-person rollup below is therefore over a
 * possibly-smaller population than the tiles above it, which is the exact
 * mismatch the documentation queue shipped. `pageCovers` decides it from the
 * server's own `total` and the strip says so; it is never inferred from the
 * page being "probably enough".
 */

/** The route's ceiling, and the server's: `completenessQuerySchema` caps at 200. */
const PAGE_SIZE = 200;

/**
 * Which summary field carries each completeness value's count.
 *
 * A `Record` over the union rather than five reads written out, so adding a
 * sixth `DocumentationCompleteness` value stops the compiler here instead of
 * silently rendering four of five and a gap.
 */
const COUNT_FIELD: Record<
  Completeness,
  'missing' | 'inProgress' | 'complete' | 'notOwed' | 'unspecified'
> = {
  missing: 'missing',
  in_progress: 'inProgress',
  complete: 'complete',
  not_owed: 'notOwed',
  unspecified: 'unspecified',
};

/**
 * A compliance figure. Takes a PHASE, never a number — so there is no signature
 * through which a caller can hand it a `?? 0`. The same component
 * `CredentialsCenter` uses, for the same reason.
 */
function Stat({ label, phase, hint }: { label: string; phase: FigurePhase; hint?: string }) {
  const populationSentence = figureNote(phase);
  return (
    <div className="cmp-stat">
      <span className="cmp-stat-value" data-state={phase.kind}>
        {figureText(phase, String)}
      </span>
      <span className="cmp-stat-label">{label}</span>
      {populationSentence ? <span className="cmp-stat-hint">{populationSentence}</span> : null}
      {hint ? <span className="cmp-stat-hint">{hint}</span> : null}
    </div>
  );
}

export function DocumentationCompleteness() {
  const permissions = usePermissions();
  const navigate = useNav();

  const page = useQuery({
    queryKey: ['documentation', 'completeness', PAGE_SIZE],
    queryFn: () => documentation.completeness({ limit: PAGE_SIZE }),
  });

  /**
   * The roster, for names — a SECONDARY read this reader may not be entitled
   * to, and the metadata record deliberately carries no name of its own.
   *
   * `DocumentationMetadataRecord` projects `workerId` and no `workerDisplayName`
   * because "the reach check that decides whether a caller may see a person's
   * name is made by the surface that renders it". This is that surface, and
   * this is that check: `mayListWorkers` is the client's single statement of
   * `directory:read` AND `user:read`, the pair `GET /directory/workers` guards
   * on. Without both the query never fires, so there is no request and no
   * cached 403, and `nameResolver` reports the missing name as a PERMISSION
   * fact rather than as a failed or pending read.
   */
  const canListWorkers = mayListWorkers(permissions);
  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);

  const counts = page.data?.counts;

  /**
   * WHOSE NUMBERS THESE ARE, read off the response that produced them.
   *
   * `counts.organizationWide` is decided by `resolveMetadataScope` on the same
   * request — the boolean spelling of the `'organization' | 'caseload'` frame
   * the other domains publish, exactly as `DocumentationListResponse.scoped` is.
   * A client-side mirror built on `usePermissions().has` would promote a
   * program-confined `documentation:read_metadata_all` to organization-wide,
   * which is the defect `readScope.ts` exists downstream of; and `can()` falls
   * back to the flattened key list whenever `permissionScopes` is absent, which
   * is knowingly permissive and correct for a GATE and wrong for a SENTENCE.
   *
   * Three values, not two. `undefined` — in flight, failed, or refused — is
   * `unknown`, and rendering it as organization-wide is the guess that does
   * damage.
   */
  const population: Population =
    counts === undefined
      ? UNKNOWN_POPULATION
      : counts.organizationWide
        ? ORGANIZATION
        : narrowedTo('the people you can see');

  const stat = (value: number | null | undefined): FigurePhase =>
    figurePhase(page, value ?? null, population);

  const records = page.data?.records ?? [];
  const rollup = useMemo(() => rollupByWorker(records), [records]);
  const covers = pageCovers(records.length, page.data?.total);

  /**
   * THE PAST-DUE TILE IS THE ONE THAT CAN LIE, AND IT IS THE ONE ANYBODY READS.
   *
   * `counts.pastDue` is `number | null`, and the null arrives with
   * `dueBasis: 'undeclared'` — the organization has not said when documentation
   * is due, so nothing HAS a due instant and nothing can have passed it.
   * `figurePhase` returns `absent` for a null inside a payload that DID arrive,
   * which renders `—` rather than `0`: the response answered, it just did not
   * answer with a number. `dueBasisNote` then says why, in words, beside it.
   *
   * A `0` here is a MEASURED zero and means something entirely different. The
   * two used to be the same glyph on this product's dashboard: `docs_overdue`
   * was `docs.filter((d) => d.is_late).length` over a column no client could
   * write, so every live tenant read "Documentation overdue: 0" as a clean
   * compliance record.
   */
  const pastDuePhase = stat(counts?.pastDue);

  /**
   * Still owed — the three outstanding values summed over the SAME population
   * the server counted, never over the page. `OUTSTANDING` is the server's own
   * set (`productionAdapters.ts`), so the tile and the model agree on what
   * "outstanding" means. `undefined` when the payload has not arrived, so the
   * tile stays a phase rather than becoming a `0 + 0 + 0`.
   */
  const stillOwed =
    counts === undefined ? undefined : counts.missing + counts.inProgress + counts.unspecified;
  const dueNote = counts ? dueBasisNote(counts.dueBasis, counts.dueWindowHours) : null;

  const columns: Column<WorkerRollup>[] = [
    {
      key: 'worker',
      header: 'Worker',
      // "Unassigned" is a claim that no worker delivered the visit, and only a
      // null `workerId` says that. Everything else goes through `nameResolver`,
      // which keeps "we could not read the roster", "the roster has not arrived"
      // and "this id is outside your scope" as three different sentences.
      render: (r) =>
        r.workerId === null ? (
          <span className="rn-muted">No worker named on the visit</span>
        ) : (
          nameOf(r.workerId)
        ),
    },
    {
      key: 'outstanding',
      header: 'Still owed',
      render: (r) => String(r.outstanding),
      numeric: true,
    },
    {
      key: 'past-due',
      header: 'Past due',
      // `null` is not zero. It means nothing in this person's rows could be
      // measured for lateness at all, which is the organization's missing
      // declaration showing through one row at a time.
      render: (r) =>
        r.pastDue === null ? <Pill variant="unsure">{NOT_MEASURABLE}</Pill> : String(r.pastDue),
      numeric: true,
    },
    {
      key: 'undeterminable',
      header: 'Lateness unknown',
      render: (r) =>
        r.undeterminable === 0 ? <span className="rn-muted">—</span> : String(r.undeterminable),
      numeric: true,
    },
    {
      key: 'oldest',
      header: 'Oldest visit',
      render: (r) =>
        r.oldestOccurredAt === null ? (
          <span className="rn-muted">—</span>
        ) : (
          new Date(r.oldestOccurredAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        ),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 960 }}>
      <h1 className="rn-card-title" style={{ fontSize: '1.125rem' }}>
        Documentation completeness
      </h1>
      <p className="rn-muted">{completenessBlurb(population.kind)}</p>

      {population.kind === 'narrowed' ? <ScopeNote scope={population.who} /> : null}

      <div className="cmp-stats">
        <Stat label="Obligations" phase={stat(counts?.obligations)} />
        <Stat label="Still owed" phase={stat(stillOwed)} />
        <Stat label="Past due" phase={pastDuePhase} />
      </div>

      {/* THE FIVE VALUES, ALL OF THEM, and the two that have no tile are the
          reason this row exists. `not_owed` is a voided obligation and
          `unspecified` is the `late` state, which says the record is open and
          says nothing about whether anything is written. Folding either into a
          tile would be this screen deciding what the schema declined to say. */}
      <ul className="doc-completeness-mix" aria-label="Documentation completeness breakdown">
        {COMPLETENESS_VALUES.map((value) => (
          <li key={value}>
            <Pill variant={completenessPillVariant(value)} dot>
              {completenessLabel(value)}
            </Pill>{' '}
            <span className={counts === undefined ? 'rn-muted' : undefined}>
              {figureText(stat(counts?.[COUNT_FIELD[value]]), String)}
            </span>
          </li>
        ))}
      </ul>

      {/* The sentence the blank tile needs. Rendered from `dueBasis`, which is
          the server's own account of where `dueAt` came from — not inferred
          from the null, so a future response that supplies one without the
          other cannot silently drop the explanation. */}
      {dueNote ? (
        <p className="rn-muted" role="note">
          {dueNote}{' '}
          {/* The fix, one click away, for the reader who can make it. Gated on
              `rules:read` because that is the key `/settings/rules` is gated on
              — offering the link to somebody who would meet the No-permission
              screen is a dead end dressed as an action. */}
          {permissions.has('rules:read') ? (
            <button type="button" className="rn-link" onClick={() => navigate('/settings/rules')}>
              Open rule settings
            </button>
          ) : null}
        </p>
      ) : null}

      {/* Counted over the whole scoped set by the server, and said so, because
          the table underneath is counted over the page. */}
      <p className="rn-muted" style={{ margin: 0 }}>
        {counts === undefined
          ? 'Counts not read yet'
          : `Counted across every delivered visit in this view${
              covers === false ? `; the table below covers the ${records.length} most recent` : ''
            }.`}
      </p>

      <ListState
        phase={listPhase(page, page.data === undefined ? undefined : rollup.length)}
        surface="table"
        rows={6}
        failedMessage="We couldn’t read documentation completeness just now. This is not a statement that nothing is outstanding."
        onRetry={() => void page.refetch()}
        refused={
          <Refused
            heading="Documentation completeness is not shown to you"
            message="Your account does not hold documentation:read_metadata, so this count was refused. It is not an organization with nothing outstanding — nobody has told you either way."
          />
        }
        empty={
          <SuccessEmpty heading="Nothing outstanding" message={emptyMessage(population.kind)} />
        }
      >
        <TableCard
          columns={columns}
          rows={rollup}
          rowKey={(r) => r.workerId ?? 'unassigned'}
          caption="Outstanding documentation by worker — status only, never note content"
        />
      </ListState>

      <p className="rn-muted" style={{ marginTop: 'var(--space-16)' }}>
        {STATUS_ONLY}{' '}
        {permissions.has('documentation:read') ? (
          <button type="button" className="rn-link" onClick={() => navigate('/documentation')}>
            Open the documentation records
          </button>
        ) : null}
      </p>
    </div>
  );
}

/** What the gate tells a reader who does not hold the key. */
export const COMPLETENESS_IS =
  'Documentation completeness shows how much documentation is outstanding, for whom, and how far past due — status only, never what a note says.';

/** The server's key for this route (documentation/routes.ts:PERMISSION_METADATA). */
export const COMPLETENESS_READ_KEY = 'documentation:read_metadata';
